import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicPosts from '../../components/cms/PublicPosts';
import PushOptIn from '../../components/cms/PushOptIn';
import {
  loadLocalSiteDesignSettings,
  loadSiteDesignSettings,
  type HomeTextPosition,
  type SiteDesignSettings,
} from '../../lib/siteSettings';
import { recordSiteVisit } from '../../lib/visitorStats';

function TextBlocks({ settings, position }: { settings: SiteDesignSettings; position: HomeTextPosition }) {
  const blocks = settings.textBlocks.filter(
    (item) => item.visible && item.position === position && item.text.trim(),
  );
  if (!blocks.length) return null;
  return (
    <div className="w-full max-w-3xl mx-auto px-3 my-4 space-y-3">
      {blocks.map((block) => (
        <div
          key={block.id}
          className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-center text-sm sm:text-base text-gray-200 whitespace-pre-line leading-7"
        >
          {block.text}
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const [now, setNow] = useState(new Date());
  const [settings, setSettings] = useState<SiteDesignSettings | null>(null);

  useEffect(() => {
    let active = true;
    const timer = setInterval(() => setNow(new Date()), 1000);

    // مهم: لا نعرض أي عنصر من عناصر الصفحة قبل وصول إعدادات الأدمن.
    // بهذا الشكل العناصر المخفية لا تظهر للحظة ثم تختفي عند أول فتح للموقع.
    void loadSiteDesignSettings()
      .then((result) => {
        if (active) setSettings(result.settings);
      })
      .catch(() => {
        // إذا تعذر الاتصال فعلياً نستخدم آخر نسخة محلية محفوظة، لكن فقط بعد
        // انتهاء محاولة التحميل حتى لا نعرض الإعدادات الافتراضية قبل إعدادات الأدمن.
        if (active) setSettings(loadLocalSiteDesignSettings());
      });

    void recordSiteVisit();
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  const menuItems = useMemo(
    () =>
      settings
        ? [...settings.menuItems].filter((item) => item.visible).sort((a, b) => a.order - b.order)
        : [],
    [settings],
  );

  const seconds = now.getSeconds().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');
  const hours = now.getHours().toString().padStart(2, '0');
  const dayNames = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
  const monthNames = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
  const dayName = dayNames[now.getDay()];
  const day = now.getDate();
  const month = monthNames[now.getMonth()];

  if (!settings) {
    return (
      <div
        className="min-h-screen grid place-items-center"
        style={{ background: 'linear-gradient(160deg, #1a1a1a 0%, #2a2a2a 50%, #1a1a1a 100%)' }}
        aria-busy="true"
        aria-label="جاري تحميل إعدادات الموقع"
      >
        <div
          className="h-9 w-9 rounded-full border-[3px] border-white/15 border-t-[#F5A623] animate-spin"
          aria-hidden="true"
        />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #1a1a1a 0%, #2a2a2a 50%, #1a1a1a 100%)' }}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-[-120px] left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #F5A623 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-[-80px] right-[-80px] w-[300px] h-[300px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #F5A623 0%, transparent 70%)' }}
        />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(#F5A623 1px, transparent 1px), linear-gradient(90deg, #F5A623 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="absolute top-4 right-4 z-20">
        <div
          className="rounded-xl px-3 py-2 flex flex-col items-center gap-0.5"
          style={{
            background: 'linear-gradient(135deg, rgba(245,166,35,0.15) 0%, rgba(255,255,255,0.05) 100%)',
            border: '1px solid rgba(245,166,35,0.3)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div className="flex items-center gap-0.5 font-mono" dir="ltr">
            <span className="text-lg font-bold text-white tracking-widest">{hours}</span>
            <span className="text-base font-bold animate-pulse text-[#F5A623]">:</span>
            <span className="text-lg font-bold text-white tracking-widest">{minutes}</span>
            <span className="text-base font-bold animate-pulse text-[#F5A623]">:</span>
            <span className="text-lg font-bold tracking-widest text-[#F5A623]">{seconds}</span>
          </div>
          <p className="text-[10px] text-gray-400 tracking-wide">
            {dayName}، {day} {month}
          </p>
        </div>
      </div>

      <header className="relative z-10 flex flex-col items-center justify-center pt-2 pb-4 px-4">
        <div
          className="flex items-center justify-center mb-0 transition-all duration-300"
          style={{
            width: `min(${settings.logoSize}px, 92vw)`,
            height: `min(${settings.logoSize}px, 92vw)`,
          }}
        >
          <img
            src="https://static.readdy.ai/image/645339dfad8e1015d6f60a0aacde7112/7ac018fe95b5893e2914eabc8075ecee.png"
            alt="Logo"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>

        <h1 className="text-xl sm:text-2xl font-bold text-white text-center tracking-wide -mt-5 mb-2">
          {settings.headingText}
        </h1>

        <TextBlocks settings={settings} position="underLogo" />
        <PublicPosts />
        <TextBlocks settings={settings} position="underAds" />
        <TextBlocks settings={settings} position="aboveIcons" />

        <div className="flex items-center gap-2 mt-2">
          <div className="h-px w-10 bg-gradient-to-r from-transparent to-[#F5A623]" />
          <p className="text-xs text-[#F5A623] tracking-widest uppercase">اختر القسم</p>
          <div className="h-px w-10 bg-gradient-to-l from-transparent to-[#F5A623]" />
        </div>
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 pb-12">
        <div className="w-full max-w-3xl grid grid-cols-2" style={{ gap: settings.gridGap }}>
          {menuItems.map((item, idx) => {
            const commonClass = `group flex flex-col items-center justify-center rounded-2xl transition-all duration-300 ${item.to ? 'cursor-pointer hover:scale-[1.02]' : 'cursor-default'}`;
            const cardStyle = {
              gap: Math.max(8, Math.round(settings.gridGap * 0.65)),
              padding: `${settings.cardPadding}px 16px`,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.03) 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
              backdropFilter: 'blur(10px)',
            } as const;

            const content = (
              <>
                <div
                  className="flex items-center justify-center rounded-2xl flex-shrink-0 transition-all duration-300 group-hover:scale-105"
                  style={{
                    width: settings.iconSize,
                    height: settings.iconSize,
                    background:
                      idx % 2 === 0
                        ? 'linear-gradient(135deg, #F5A623, #e09510)'
                        : 'linear-gradient(135deg, #3a3a3a, #2a2a2a)',
                    boxShadow:
                      idx % 2 === 0
                        ? '0 8px 32px rgba(245,166,35,0.4)'
                        : '0 8px 32px rgba(0,0,0,0.4)',
                    border: idx % 2 === 0 ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <i
                    className={item.icon}
                    style={{
                      color: idx % 2 === 0 ? '#fff' : '#F5A623',
                      fontSize: Math.round(settings.iconSize * 0.48),
                    }}
                  />
                </div>

                <div className="flex flex-col items-center text-center">
                  <p className="text-base font-bold text-white transition-colors duration-200 group-hover:text-[#F5A623] leading-snug">
                    {item.label}
                  </p>
                  {item.comingSoon ? (
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed flex items-center gap-1.5">
                      <span>{item.desc}</span>
                      <i className="ri-time-line text-sm" aria-hidden="true" />
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">{item.desc}</p>
                  )}
                </div>
              </>
            );

            return item.to ? (
              <Link key={item.id} to={item.to} className={commonClass} style={cardStyle}>
                {content}
              </Link>
            ) : (
              <div key={item.id} className={commonClass} style={cardStyle}>
                {content}
              </div>
            );
          })}
        </div>
        <TextBlocks settings={settings} position="belowIcons" />
      </main>

      <footer className="relative z-10 py-5 text-center border-t border-white/5">
        <p className="text-xs text-gray-500">
          تم البرمجة من قبل <span className="font-bold text-[#F5A623]">Murthdha abdulzahra</span>
        </p>
      </footer>
      <PushOptIn />
    </div>
  );
}
